import { useCallback, useState } from 'react';
import type { FormInstance } from 'antd';
import { useLazyListPlotsQuery, useLazyListBedsByPlotQuery, useCreateBedMutation } from '../../../../../api/fieldsApi';
import type { Plot } from '../../../../../models/Plot';
import type { Bed } from '../../../../../models/Bed';
import { toastError } from '../../../../../utils/toast';
import type { LockedLocationIds, PlantingFormModalValues } from '../types';

interface UseLocationCascadeOptions {
  form: FormInstance<PlantingFormModalValues>;
  lockedLocation: LockedLocationIds | null;
}

interface UseLocationCascadeResult {
  plotsByField: Record<string, Plot[]>;
  bedsByPlot: Record<string, Bed[]>;
  plotsLoading: boolean;
  bedsLoading: boolean;
  setPlotsByField: React.Dispatch<React.SetStateAction<Record<string, Plot[]>>>;
  setBedsByPlot: React.Dispatch<React.SetStateAction<Record<string, Bed[]>>>;
  ensureBedsForPlot: (plotId: number | string) => Promise<Bed[]>;
  handleFieldChange: (formIndex: number, fieldId: number | string | undefined) => Promise<void>;
  handlePlotChange: (formIndex: number, plotId: number | string | undefined) => Promise<void>;
  loadPlotsForField: (fieldId: number | string) => Promise<Plot[]>;
}

export const useLocationCascade = ({
  form,
  lockedLocation,
}: UseLocationCascadeOptions): UseLocationCascadeResult => {
  const [loadPlots] = useLazyListPlotsQuery();
  const [loadBeds] = useLazyListBedsByPlotQuery();
  const [createBed] = useCreateBedMutation();

  const [plotsByField, setPlotsByField] = useState<Record<string, Plot[]>>({});
  const [bedsByPlot, setBedsByPlot] = useState<Record<string, Bed[]>>({});
  const [plotsLoading, setPlotsLoading] = useState(false);
  const [bedsLoading, setBedsLoading] = useState(false);

  const ensureBedsForPlot = useCallback(
    async (plotId: number | string) => {
      try {
        setBedsLoading(true);
        let beds = await loadBeds(plotId).unwrap();
        if (!Array.isArray(beds) || beds.length === 0) {
          const created = await createBed({ plotId: String(plotId) }).unwrap();
          beds = created ? [created] : [];
        }
        setBedsByPlot((prev) => ({ ...prev, [String(plotId)]: beds }));
        return beds;
      } catch (error) {
        toastError(error);
        return [];
      } finally {
        setBedsLoading(false);
      }
    },
    [createBed, loadBeds],
  );

  const loadPlotsForField = useCallback(
    async (fieldId: number | string) => {
      try {
        setPlotsLoading(true);
        const plots = await loadPlots(fieldId).unwrap();
        setPlotsByField((prev) => ({ ...prev, [String(fieldId)]: plots }));
        return plots;
      } catch (error) {
        toastError(error);
        return [];
      } finally {
        setPlotsLoading(false);
      }
    },
    [loadPlots],
  );

  const handleFieldChange = useCallback(
    async (formIndex: number, fieldId: number | string | undefined) => {
      if (lockedLocation) return;
      form.setFields([
        { name: ['locations', formIndex, 'plotId'], value: undefined },
        { name: ['locations', formIndex, 'bedId'], value: undefined },
      ]);
      if (!fieldId) return;
      await loadPlotsForField(fieldId);
    },
    [form, loadPlotsForField, lockedLocation],
  );

  const handlePlotChange = useCallback(
    async (formIndex: number, plotId: number | string | undefined) => {
      if (lockedLocation) return;
      form.setFields([{ name: ['locations', formIndex, 'bedId'], value: undefined }]);
      if (!plotId) return;
      const beds = await ensureBedsForPlot(plotId);
      if (beds.length > 0) {
        form.setFields([
          { name: ['locations', formIndex, 'bedId'], value: beds[0].id },
        ]);
      }
    },
    [form, ensureBedsForPlot, lockedLocation],
  );

  return {
    plotsByField,
    bedsByPlot,
    plotsLoading,
    bedsLoading,
    setPlotsByField,
    setBedsByPlot,
    ensureBedsForPlot,
    handleFieldChange,
    handlePlotChange,
    loadPlotsForField,
  };
};
